import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { circleUsersQuery, forViewer, viewerIdOf } from "../services/queries";

interface User {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  status: "active" | "inactive";
  joinedAt: string;
}

export const useSchoolUsers = () => {
  const [schoolUsers, setSchoolUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user: authUser, token } = useAuth();

  const loadSchoolUsers = useCallback(async () => {
    if (!authUser?.schoolId || !token) return;

    try {
      setIsLoading(true);
      setError(null);
      const users = await api.users.getAll(authUser.schoolId, token);
      setSchoolUsers(users);
    } catch (err: any) {
      console.error("Failed to load school users:", err);
      setError("Failed to load school users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [authUser?.schoolId, token]);

  useEffect(() => {
    loadSchoolUsers();
  }, [loadSchoolUsers]);

  return {
    schoolUsers,
    isLoading,
    error,
    refetch: loadSchoolUsers,
  };
};

/** One empty list while loading, so effects that depend on the members don't re-run every render. */
const NO_CIRCLE_USERS: never[] = [];

export const useCircleUsers = (subcircleId: string) => {
  const { user: authUser, token } = useAuth();

  // react-query-backed (not bespoke state) so that two components mounted
  // at once for the same subcircle — e.g. CircleHomePage and
  // ManageCircleUsers — share one request instead of firing a duplicate.
  const { data, isLoading, error, refetch } = useQuery({
    ...forViewer(circleUsersQuery(subcircleId, authUser?.schoolId, token), viewerIdOf(authUser)),
    enabled: !!authUser?.schoolId && !!token && !!subcircleId,
  });

  return {
    circleUsers: data ?? NO_CIRCLE_USERS,
    isLoading,
    error: error ? "Failed to load circle users. Please try again." : null,
    refetch,
  };
};
